import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Menu, Search, User, ShoppingBag, X, ChevronDown, ChevronRight,
  Instagram, Facebook, Youtube, ArrowLeft, Heart,
} from 'lucide-react';
import { getNavCategories } from '../services/api';
import { useSiteConfig } from '../context/SiteConfigContext';
import { useWishlist } from '../context/WishlistContext';
import { useAuth } from '../context/AuthContext';

export default function Navbar({ cartCount = 0, onOpenCart, onOpenSearch }) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState('main');
  const [catOpen, setCatOpen] = useState(false);
  const navigate = useNavigate();
  const { config } = useSiteConfig();
  const { count: wishCount } = useWishlist();
  const { isAuthed } = useAuth();

  const [navCategories, setNavCategories] = useState([]);

  useEffect(() => {
    getNavCategories().then((data) => {
      console.log('Categories from API:', data);
      setNavCategories(data);
    });
  }, []);

  const closeAll = () => { setOpen(false); setView('main'); };

  // ✅ Correct slug generator – removes apostrophes, keeps only alnum + spaces
  const getCategorySlug = (category) => {
    if (category.slug) {
      return category.slug;
    }
    console.warn(`Category "${category.name}" has no slug – generating from name.`);
    return category.name
      .toLowerCase()
      .replace(/['"]/g, '')          // remove apostrophes & quotes
      .replace(/[^a-z0-9\s]+/g, '')  // remove other punctuation, keep spaces
      .trim()
      .replace(/\s+/g, '-');         // collapse spaces to single hyphens
  };

  const BrandMark = () => {
    if (!config) {
      return (
        <div className="flex items-center gap-2 select-none lg:mr-8 h-10 sm:h-12 w-32 animate-pulse">
          <div className="h-full w-full bg-neutral-200 rounded" />
        </div>
      );
    }

    const brand = config.brand || 'Victoria Resin Store';

    return (
      <Link to="/" className="flex items-center gap-2 select-none lg:mr-8">
        {config.logoUrl ? (
          <img src={config.logoUrl} alt={brand} className="h-10 sm:h-12 w-auto" />
        ) : (
          <>
            <span className="font-serif italic text-3xl sm:text-4xl text-[#b8935a] tracking-wide leading-none">
              {brand.split(' ')[0] || ''}
            </span>
            <span className="h-7 w-px bg-neutral-400" />
            <span className="text-xs tracking-[0.25em] text-[#b8935a] font-medium leading-tight">
              {(brand.split(' ').slice(1).join(' ') || 'RESIN STORE').split(' ').map((w, i, arr) => (
                <React.Fragment key={i}>
                  {w.toUpperCase()}
                  {i < arr.length - 1 && <br />}
                </React.Fragment>
              ))}
            </span>
          </>
        )}
      </Link>
    );
  };

  const announcements = config?.announcements?.length
    ? config.announcements
    : ['GET EXTRA 5% OFF ON ALL PREPAID ORDERS', 'SALE UPTO 70% OFF'];

  const socials = config?.socials || {};

  return (
    <>
      <div className="bg-[#1e3a2b] text-white text-xs sm:text-sm overflow-hidden">
        <div className="whitespace-nowrap animate-[marquee_28s_linear_infinite] py-2">
          {[...announcements, ...announcements, ...announcements].map((text, i) => (
            <span key={i} className="mx-8 font-semibold">{text}</span>
          ))}
        </div>
      </div>

      <header className="sticky top-0 z-40 bg-white border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 lg:h-20 flex items-center justify-between">
          <button
            aria-label="Open menu"
            className="p-2 -ml-2 lg:hidden hover:bg-neutral-100 rounded-md transition-colors"
            onClick={() => { setOpen(true); setView('main'); }}
          >
            <Menu className="w-6 h-6 text-[#1e3a2b]" />
          </button>

          <BrandMark />

          <nav className="hidden lg:flex items-center gap-8 text-sm font-medium text-[#1e3a2b] flex-1">
            <Link to="/" className="hover:text-[#b8935a] transition-colors">Home</Link>
            <Link to="/collection/all" className="hover:text-[#b8935a] transition-colors">Best Sellers</Link>
            <div
              className="relative"
              onMouseEnter={() => setCatOpen(true)}
              onMouseLeave={() => setCatOpen(false)}
            >
              <button className="flex items-center gap-1 hover:text-[#b8935a] transition-colors">
                Category <ChevronDown className={`w-4 h-4 transition-transform ${catOpen ? 'rotate-180' : ''}`} />
              </button>
              {catOpen && (
                <div className="absolute top-full left-0 pt-2 min-w-[260px]">
                  <div className="bg-white shadow-xl border border-neutral-100 rounded-md py-2 animate-[fadeIn_0.15s_ease-out]">
                    {navCategories.map(c => {
                      const slug = getCategorySlug(c);
                      return (
                        <Link
                          key={c.id}
                          to={`/collection/${slug}`}
                          onClick={() => setCatOpen(false)}
                          className="block px-4 py-2.5 text-sm text-neutral-800 hover:bg-neutral-50 hover:text-[#b8935a] transition-colors"
                        >
                          {c.name}
                        </Link>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            <Link to="/collection/raw-material" className="hover:text-[#b8935a] transition-colors">Raw Material</Link>
            <Link to="/collection/course" className="hover:text-[#b8935a] transition-colors">Course</Link>
            <Link to="/contact" className="hover:text-[#b8935a] transition-colors">Contact Us</Link>
            <Link to="/pages/about-us" className="hover:text-[#b8935a] transition-colors">About Us</Link>
          </nav>

          <div className="flex items-center gap-1 sm:gap-3">
            {/* 🆕 Student/Seller Login button (desktop) */}
            <a
              href="https://ecomapi.victoriaresinstore.com/admin/login"
              className="hidden lg:inline-block text-sm font-medium text-[#1e3a2b] hover:text-[#b8935a] transition-colors px-2 py-1"
            >
             Student Seller Login
            </a>

            <button onClick={onOpenSearch} className="p-2 hover:bg-neutral-100 rounded-full transition-colors" aria-label="Search">
              <Search className="w-5 h-5 text-[#1e3a2b]" />
            </button>
            <Link to="/account/wishlist" className="relative p-2 hover:bg-neutral-100 rounded-full transition-colors hidden sm:inline-flex" aria-label="Wishlist">
              <Heart className="w-5 h-5 text-[#1e3a2b]" />
              {wishCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                  {wishCount}
                </span>
              )}
            </Link>
            <Link to="/account" className="p-2 hover:bg-neutral-100 rounded-full transition-colors hidden sm:inline-flex" aria-label="Account">
              <User className="w-5 h-5 text-[#1e3a2b]" />
            </Link>
            <button
              onClick={onOpenCart}
              className="relative p-2 hover:bg-neutral-100 rounded-full transition-colors"
              aria-label="Cart"
            >
              <ShoppingBag className="w-5 h-5 text-[#1e3a2b]" />
              {cartCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-[#b8935a] text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={closeAll} />
          <div className="absolute left-0 top-0 h-full w-[85%] max-w-sm bg-[#1e3a2b] text-white flex flex-col animate-[slideIn_0.25s_ease-out] overflow-hidden">

            {/* Main view */}
            <div
              className={`absolute inset-0 flex flex-col transition-transform duration-300 ${view === 'main' ? 'translate-x-0' : '-translate-x-full'}`}
            >
              <div className="flex items-center justify-between px-6 h-16 border-b border-white/10">
                <span className="font-semibold tracking-widest text-lg">EXPLORE</span>
                <button onClick={closeAll} className="p-2 hover:bg-white/10 rounded-full">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <nav className="flex-1 overflow-y-auto py-2">
                <button
                  onClick={() => { closeAll(); navigate('/'); }}
                  className="w-full text-left px-6 py-3.5 text-white text-base hover:bg-white/5 transition-colors"
                >Home</button>
                <button
                  onClick={() => { closeAll(); navigate('/collection/all'); }}
                  className="w-full text-left px-6 py-3.5 text-white text-base hover:bg-white/5 transition-colors"
                >Best Sellers</button>
                <button
                  onClick={() => setView('category')}
                  className="w-full flex items-center justify-between px-6 py-3.5 text-white text-base hover:bg-white/5 transition-colors"
                >
                  <span>Category</span>
                  <ChevronRight className="w-4 h-4" />
                </button>

                <button
                  onClick={() => { closeAll(); navigate('/collection/raw-material'); }}
                  className="w-full text-left px-6 py-3.5 text-white text-base hover:bg-white/5 transition-colors"
                >Raw Material</button>

                <button
                  onClick={() => { closeAll(); navigate('/collection/course'); }}
                  className="w-full text-left px-6 py-3.5 text-white text-base hover:bg-white/5 transition-colors"
                >Course</button>

                <button
                  onClick={() => { closeAll(); navigate('/contact'); }}
                  className="w-full text-left px-6 py-3.5 text-white text-base hover:bg-white/5 transition-colors"
                >Contact Us</button>
                <button
                  onClick={() => { closeAll(); navigate('/pages/about-us'); }}
                  className="w-full text-left px-6 py-3.5 text-white text-base hover:bg-white/5 transition-colors"
                >About Us</button>

                {/* 🆕 Student/Seller Login (mobile) */}
                <a
                  href="https://ecomapi.victoriaresinstore.com/admin/login"
                  className="block w-full text-left px-6 py-3.5 text-white text-base hover:bg-white/5 transition-colors"
                >
                  Student Seller Login
                </a>

                <div className="mt-4 border-t border-white/10 pt-4">
                  <button
                    onClick={() => { closeAll(); navigate(isAuthed ? '/account' : '/login'); }}
                    className="w-full text-left px-6 py-3.5 text-white text-base hover:bg-white/5 transition-colors flex items-center gap-3"
                  >
                    <User className="w-5 h-5" /> {isAuthed ? 'Account' : 'Log in'}
                  </button>
                </div>
              </nav>
              <div className="px-6 py-5 border-t border-white/10 flex items-center gap-4">
                <a href={socials.facebook || '#'} aria-label="Facebook" className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 grid place-items-center transition-colors">
                  <Facebook className="w-4 h-4" />
                </a>
                <a href={socials.instagram || '#'} aria-label="Instagram" className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 grid place-items-center transition-colors">
                  <Instagram className="w-4 h-4" />
                </a>
                <a href={socials.youtube || '#'} aria-label="Youtube" className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 grid place-items-center transition-colors">
                  <Youtube className="w-4 h-4" />
                </a>
              </div>
            </div>

            {/* Category sub-view */}
            <div
              className={`absolute inset-0 flex flex-col bg-[#1e3a2b] transition-transform duration-300 ${view === 'category' ? 'translate-x-0' : 'translate-x-full'}`}
            >
              <div className="flex items-center px-6 h-16 border-b border-white/10 gap-4">
                <button onClick={() => setView('main')} className="p-1 hover:bg-white/10 rounded-full" aria-label="Back">
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <span className="font-semibold text-lg">Category</span>
              </div>
              <nav className="flex-1 overflow-y-auto py-2">
                {navCategories.map(c => {
                  const slug = getCategorySlug(c);
                  return (
                    <button
                      key={c.id}
                      onClick={() => { closeAll(); navigate(`/collection/${slug}`); }}
                      className="w-full text-left px-6 py-3.5 text-white text-base hover:bg-white/5 transition-colors"
                    >
                      {c.name}
                    </button>
                  );
                })}
              </nav>
            </div>

          </div>
        </div>
      )}
    </>
  );
}